import { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import type {
  ActionRecord,
  AssistantProfile,
  Conversation,
  ConversationRecordReference,
  Message,
  ResultExportPolicy,
  SalesforceContext,
  UserProfile
} from "@ai-sales/contracts";
import { ActionProposal } from "./ActionProposal.js";
import { ApiRequestError, api, type SalesforceBusinessAccess, type SessionBootstrap, type SessionChannel } from "./api.js";
import { useSalesforceBridge } from "./bridge.js";
import { createClientId } from "./client-id.js";
import { ConversationContextPanel } from "./ConversationContextPanel.js";
import { AssistantUserSettings } from "./AssistantUserSettings.js";
import { HistoryIcon, LayersIcon, MicIcon, PaperclipIcon, SendIcon, SettingsIcon, SparklesIcon } from "./icons.js";
import { UserAvatar } from "./UserAvatar.js";
import { toCustomerFacingError, WebsiteMessageContent } from "./WebsiteMessageContent.js";
import { useWebsiteInactivityFollowUp } from "./website-follow-up.js";
import {
  describeContextFocus,
  selectEffectiveContext,
  type ContextSourceMode
} from "./salesforce-context.js";
import { conversationDateGroup, formatConversationTime, shouldShowMessageTime } from "./conversation-time.js";
import { microphoneStartErrorMessage } from "./media-permissions.js";

interface UploadedAttachment {
  id: string;
  fileName: string;
  mimeType: string;
  size: number;
  previewUrl?: string;
}

const maxAttachmentBytes = 20 * 1024 * 1024;
const maxTotalAttachmentBytes = 40 * 1024 * 1024;
const maxConversationAttachments = 3;
const attachmentAccept = ".pdf,.jpg,.jpeg,.png,.txt,.csv,.xlsx,application/pdf,image/jpeg,image/png,text/plain,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const supportedAttachmentMimeTypes = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
  "text/plain",
  "text/csv",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
]);
const maxVoiceRecordingMs = 60_000;
const genericConversationTitles = new Set(["销售助理对话", "AI 助手对话", "新工作会话", "新对话"]);
const generationStages = [
  "正在理解你的需求",
  "正在整理会话上下文",
  "正在组织清晰的回答"
];
const attachmentGenerationStages = [
  "正在读取截图和文字说明",
  "正在识别目标记录与字段",
  "正在准备安全预览"
];
const ambiguousConnectionErrorPattern = /Failed to fetch|NetworkError|网络|连接中断/i;
const actionStateLabels: Record<ActionRecord["state"], string> = {
  draft: "草稿", pending_confirmation: "待确认", needs_attention: "需要处理", executing: "执行中",
  succeeded: "已完成", failed: "未完成", cancelled: "已取消", expired: "已过期"
};

function isLocalDevelopmentRuntime(): boolean {
  return ["localhost", "127.0.0.1"].includes(window.location.hostname);
}

function conversationTitleFromMessage(content: string): string {
  const normalized = content.replace(/\s+/g, " ").trim();
  return normalized.length > 30 ? `${normalized.slice(0, 30)}…` : normalized;
}

function conversationStatusFromActions(actions: ActionRecord[]): Conversation["status"] {
  if (actions.some(({ state }) => state === "needs_attention" || state === "failed")) return "needs_attention";
  if (actions.some(({ state }) => state === "pending_confirmation")) return "pending_confirmation";
  if (actions.length && actions.every(({ state }) => ["succeeded", "cancelled", "expired"].includes(state))) {
    return "completed";
  }
  return "in_progress";
}

function requiresClientPreview(action: ActionRecord): boolean {
  return (action.plan.operation === "update" || action.plan.operation === "delete") &&
    !action.plan.expectedLastModifiedDate && !action.plan.batchRecords?.length;
}

async function fileToBase64(file: Blob): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  const chunkSize = 32_768;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, Math.min(offset + chunkSize, bytes.length)));
  }
  return btoa(binary);
}

async function fileSha256(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), (value) => value.toString(16).padStart(2, "0")).join("");
}

function attachmentMimeType(file: Pick<File, "name" | "type">): string {
  if (file.type) return file.type.toLowerCase();
  const lowerName = file.name.toLowerCase();
  if (lowerName.endsWith(".pdf")) return "application/pdf";
  if (lowerName.endsWith(".jpg") || lowerName.endsWith(".jpeg")) return "image/jpeg";
  if (lowerName.endsWith(".png")) return "image/png";
  if (lowerName.endsWith(".txt")) return "text/plain";
  if (lowerName.endsWith(".csv")) return "text/csv";
  if (lowerName.endsWith(".xlsx")) return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  return "";
}

function formatAttachmentSize(size: number): string {
  return size >= 1024 * 1024
    ? `${(size / 1024 / 1024).toFixed(size >= 10 * 1024 * 1024 ? 0 : 1)} MB`
    : `${Math.max(1, Math.round(size / 1024))} KB`;
}

function attachmentTypeLabel(mimeType: string): string {
  if (mimeType.startsWith("image/")) return "图片";
  if (mimeType === "application/pdf") return "PDF";
  if (mimeType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") return "XLSX";
  if (mimeType === "text/csv") return "CSV";
  return "TXT";
}

export function extractClipboardFiles(clipboardData: Pick<DataTransfer, "files" | "items">): File[] {
  const directFiles = Array.from(clipboardData.files ?? []);
  const itemFiles = directFiles.length
    ? []
    : Array.from(clipboardData.items ?? [])
      .filter(({ kind }) => kind === "file")
      .flatMap((item) => {
        const file = item.getAsFile();
        return file ? [file] : [];
      });
  const unique = new Map<string, File>();
  for (const file of [...directFiles, ...itemFiles]) {
    unique.set(`${file.name}:${file.size}:${file.type}:${file.lastModified}`, file);
  }
  return [...unique.values()];
}

export function attachmentSelectionError(
  existing: Array<Pick<UploadedAttachment, "size">>,
  files: Array<Pick<File, "name" | "size" | "type">>
): string | undefined {
  if (existing.length + files.length > maxConversationAttachments) return "每条消息最多添加 3 个附件";
  const oversized = files.find(({ size }) => size > maxAttachmentBytes);
  if (oversized) return `附件“${oversized.name}”超过 20MB`;
  if (files.some((file) => !supportedAttachmentMimeTypes.has(attachmentMimeType(file)))) {
    return "仅支持 PDF、JPG、PNG、TXT、CSV 或 XLSX 文件";
  }
  const nextTotal = existing.reduce((total, attachment) => total + attachment.size, 0) +
    files.reduce((total, file) => total + file.size, 0);
  return nextTotal > maxTotalAttachmentBytes ? "每条消息的附件总量不能超过 40MB" : undefined;
}

const createWelcomeMessage = (websiteMode: boolean): Message => ({
  id: createClientId(),
  conversationId: createClientId(),
  role: "assistant",
  content: websiteMode
    ? "您好，我是官网服务助手。您可以咨询工商业或源网侧储能方案、下载产品资料，也可以提交项目需求，由销售团队跟进。"
    : "你好，我是 AI 助手。无需先打开某条记录，你可以直接查询、创建或更新潜在客户、业务机会、销售拜访、跟进任务和报价资料；涉及写入时会先展示预览并请你确认。",
  createdAt: new Date().toISOString()
});

function GeneratingIndicator({ hasAttachments = false }: { hasAttachments?: boolean }) {
  const [stageIndex, setStageIndex] = useState(0);
  const stages = hasAttachments ? attachmentGenerationStages : generationStages;
  useEffect(() => {
    const timer = window.setInterval(() => {
      setStageIndex((current) => (current + 1) % stages.length);
    }, 1_800);
    return () => window.clearInterval(timer);
  }, [stages.length]);

  return (
    <div className="generating" role="status" aria-live="polite" aria-atomic="true">
      <span className="generating__orb" aria-hidden="true">
        <SparklesIcon size={18} />
        <i />
      </span>
      <span className="generating__copy">
        <strong>已收到，正在处理</strong>
        <small key={stageIndex}>{stages[stageIndex]}</small>
      </span>
      <span className="generating__signal" aria-hidden="true">
        <i /><i /><i /><i />
      </span>
    </div>
  );
}

export function AttachmentPicker({
  disabled,
  busy,
  onSelect
}: {
  disabled: boolean;
  busy: boolean;
  onSelect: (files: File[]) => void | Promise<void>;
}) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const label = busy ? "正在上传附件" : "添加附件";

  return (
    <label
      className={`icon-button attachment-picker${disabled ? " attachment-picker--disabled" : ""}`}
      htmlFor={inputId}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      aria-label={label}
      title={busy ? "正在上传附件，请稍候" : "添加图片、PDF 或文本，也可在输入框粘贴（单个不超过20MB）"}
      onKeyDown={(event) => {
        if (disabled || !["Enter", " "].includes(event.key)) return;
        event.preventDefault();
        inputRef.current?.click();
      }}
      onClick={(event) => {
        if (disabled) event.preventDefault();
      }}
    >
      <input
        id={inputId}
        ref={inputRef}
        className="visually-hidden"
        type="file"
        multiple
        accept={attachmentAccept}
        disabled={disabled}
        aria-label="选择附件文件"
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          event.target.value = "";
          void onSelect(files);
        }}
      />
      <PaperclipIcon />
    </label>
  );
}

export function assistantMessageRetryIsSafe(error: unknown): boolean {
  return error instanceof ApiRequestError &&
    (error.status < 500 || error.code === "MESSAGE_NOT_SAVED");
}

export function isOutcomeActionMessage(
  websiteMode: boolean,
  role: Message["role"],
  actionState?: ActionRecord["state"]
): boolean {
  return !websiteMode && role === "assistant" &&
    actionState !== undefined && ["succeeded", "failed"].includes(actionState);
}

export function assistantErrorPresentation(error: string): { title: string; description: string } {
  if (/timeout|timed?\s*out|超时|aborted due to timeout/i.test(error)) {
    return {
      title: "模型规划超时",
      description: "本次请求尚未保存，也没有修改任何记录。你可以直接重新发送，或补充更明确的目标记录和字段。"
    };
  }
  if (/模型本次未返回有效结果|未返回结构化规划结果|模型.*空结果/i.test(error)) {
    return {
      title: "模型本次未返回结果",
      description: "系统已自动重试，但模型仍未返回可处理的内容。本次消息尚未保存，也没有修改任何记录；你可以直接重新发送。"
    };
  }
  if (/场景未分配|SCENARIO_NOT_ASSIGNED/.test(error)) {
    return {
      title: "当前查询能力需要刷新",
      description: "系统未能把这次基础查询匹配到当前用户已授权的通用能力。请重新发送；如仍失败，请联系管理员重新分析并保存 AI 助手扫描范围。"
    };
  }
  if (/CSRF|安全会话/.test(error)) {
    return {
      title: "会话安全状态已更新",
      description: "刚才的消息没有提交。请重新发送；如仍失败，请刷新 AI 助手。"
    };
  }
  if (/附件已上传|附件.*模型|图文请求|附件.*识别/.test(error)) {
    if (/操作结构未通过安全校验|返回结果未通过安全格式校验|结构化规划/.test(error)) {
      return {
        title: "模型结果格式未通过校验",
        description: error
      };
    }
    return {
      title: "附件识别未完成",
      description: error
    };
  }
  if (/会话已过期|未登录|重新验证|需要访问验证/.test(error)) {
    return {
      title: "需要重新连接",
      description: "当前会话已失效，请刷新 AI 助手后继续，已输入的内容不会自动写入 Salesforce。"
    };
  }
  if (ambiguousConnectionErrorPattern.test(error)) {
    return {
      title: "连接中断，结果待确认",
      description: "暂时无法确认刚才的请求是否已经送达。系统不会自动重发，以免重复操作；请先查看当前会话和 Salesforce 结果。"
    };
  }
  return { title: "未能完成操作", description: error };
}

export function AssistantApp() {
  const embeddedSalesforceMode = window.parent !== window;
  const platformWebMode = window.parent === window && window.location.pathname.startsWith("/workspace");
  const websiteMode = window.parent === window && !platformWebMode && !window.location.pathname.startsWith("/embed/salesforce/");
  const standaloneSalesforceMode = window.parent === window && window.location.pathname.startsWith("/embed/salesforce/");
  const bridge = useSalesforceBridge();
  const {
    connected, context, sessionChannel, consentRequired, setConsentRequired,
    setContext, setConnected, executeAction, previewAction, send,
    actionCompletion, setActionCompletion, previewCompletion, setPreviewCompletion,
    previewFailure, setPreviewFailure
  } = bridge;
  const [conversation, setConversation] = useState<Conversation>();
  const [messages, setMessages] = useState<Message[]>(() => [createWelcomeMessage(websiteMode)]);
  const [actions, setActions] = useState<Record<string, ActionRecord>>({});
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [canRetryMessage, setCanRetryMessage] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile>();
  const [assistantProfile, setAssistantProfile] = useState<AssistantProfile>();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [profileBusy, setProfileBusy] = useState(false);
  const [manualSignedOut, setManualSignedOut] = useState(false);
  const [channel, setChannel] = useState<SessionChannel>();
  api.setSessionChannelHint(channel ?? (websiteMode ? "website" : platformWebMode ? "platform_web" : "salesforce"));
  const [accessRequired, setAccessRequired] = useState(false);
  const [verificationStep, setVerificationStep] = useState<"email" | "code">("email");
  const [verificationEmail, setVerificationEmail] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [resendSeconds, setResendSeconds] = useState(0);
  const [authBusy, setAuthBusy] = useState(false);
  const [resourceNotice, setResourceNotice] = useState("");
  const [attachments, setAttachments] = useState<UploadedAttachment[]>([]);
  const [attachmentsEnabled, setAttachmentsEnabled] = useState(true);
  const [resultExportPolicy, setResultExportPolicy] = useState<ResultExportPolicy>();
  const [objectExportPolicies, setObjectExportPolicies] = useState<Record<string, "inherit" | "allow" | "deny">>({});
  const [attachmentBusy, setAttachmentBusy] = useState(false);
  const [attachmentUploadLabel, setAttachmentUploadLabel] = useState("");
  const [pendingSubmission, setPendingSubmission] = useState<{ content: string; attachmentNames: string[] }>();
  const [previewIssues, setPreviewIssues] = useState<Record<string, string>>({});
  const [voiceState, setVoiceState] = useState<"idle" | "recording" | "transcribing">("idle");
  const [voiceAvailability, setVoiceAvailability] = useState<{ available: boolean; reason?: string }>();
  const [platformDisplayName, setPlatformDisplayName] = useState("");
  const [actionCenterOpen, setActionCenterOpen] = useState(false);
  const [contextManagerOpen, setContextManagerOpen] = useState(false);
  const [contextSourceMode, setContextSourceMode] = useState<ContextSourceMode>("page");
  const [contextReferences, setContextReferences] = useState<ConversationRecordReference[]>([]);
  const [actionCenterItems, setActionCenterItems] = useState<ActionRecord[]>([]);
  const [workRecordTab, setWorkRecordTab] = useState<"conversations" | "attention">("conversations");
  const [conversationItems, setConversationItems] = useState<Conversation[]>([]);
  const [conversationSearch, setConversationSearch] = useState("");
  const [messageHasMore, setMessageHasMore] = useState(false);
  const [messageCursor, setMessageCursor] = useState<string>();
  const [messagePageBusy, setMessagePageBusy] = useState(false);
  const [businessAccess, setBusinessAccess] = useState<SalesforceBusinessAccess>();
  const [businessAuthorizationBusy, setBusinessAuthorizationBusy] = useState(false);
  const [businessAuthorizationPending, setBusinessAuthorizationPending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const voiceChunksRef = useRef<Blob[]>([]);
  const voiceStopTimerRef = useRef<number | null>(null);
  const initializedRef = useRef(false);
  const localSessionStartedRef = useRef(false);
  const lastSyncedContextRef = useRef("");
  const previewRequestsRef = useRef(new Set<string>());
  const preserveScrollRef = useRef<{ height: number; top: number } | undefined>(undefined);
  const attachmentPreviewUrlsRef = useRef(new Set<string>());

  const revokeAttachmentPreview = useCallback((previewUrl?: string) => {
    if (!previewUrl) return;
    URL.revokeObjectURL(previewUrl);
    attachmentPreviewUrlsRef.current.delete(previewUrl);
  }, []);

  const clearAttachmentPreviews = useCallback(() => {
    for (const previewUrl of attachmentPreviewUrlsRef.current) URL.revokeObjectURL(previewUrl);
    attachmentPreviewUrlsRef.current.clear();
  }, []);

  useEffect(() => () => clearAttachmentPreviews(), [clearAttachmentPreviews]);

  const refreshBusinessAccess = useCallback(async () => {
    const result = await api.getSalesforceBusinessAccess();
    setBusinessAccess(result);
    if (result.ready) setBusinessAuthorizationPending(false);
    return result;
  }, []);

  const beginBusinessAuthorization = useCallback(async () => {
    if (businessAuthorizationBusy) return;
    const popup = embeddedSalesforceMode
      ? window.open("about:blank", "ai-salesforce-business-authorization", "popup,width=720,height=780")
      : null;
    if (embeddedSalesforceMode && !popup) {
      setError("浏览器阻止了 Salesforce 授权窗口，请允许此站点打开弹窗后重试");
      return;
    }
    setBusinessAuthorizationBusy(true);
    setError("");
    try {
      const returnPath = embeddedSalesforceMode
        ? "/auth/salesforce/complete"
        : "/workspace?salesforceAuthorization=complete";
      const authorization = await api.startSalesforceGrant("business_data", returnPath);
      if (popup) {
        popup.opener = null;
        popup.location.replace(authorization.authorizationUrl);
        setBusinessAuthorizationPending(true);
      } else {
        window.location.assign(authorization.authorizationUrl);
      }
    } catch (cause) {
      popup?.close();
      setError(cause instanceof Error ? cause.message : "Salesforce 授权发起失败");
    } finally {
      setBusinessAuthorizationBusy(false);
    }
  }, [businessAuthorizationBusy, embeddedSalesforceMode]);

  const appendWebsiteFollowUp = useCallback((content: string) => {
    if (!conversation) return;
    setMessages((current) => [...current, {
      id: createClientId(),
      conversationId: conversation.id,
      role: "assistant",
      content,
      createdAt: new Date().toISOString()
    }]);
  }, [conversation]);

  const restoreConversation = useCallback(async (selected: Conversation) => {
    const [page, references] = await Promise.all([
      api.listMessagePage(selected.id, { limit: 30 }),
      api.listConversationReferences(selected.id)
    ]);
    const actionIds = [...new Set(page.items.flatMap((message) => message.actionId ? [message.actionId] : []))];
    const restoredActions = await Promise.all(actionIds.map((id) => api.getAction(id)));
    setConversation(selected);
    setMessages(page.items.length ? page.items : [createWelcomeMessage(websiteMode)]);
    setMessageHasMore(page.hasMore);
    setMessageCursor(page.nextCursor);
    setContextReferences(references);
    setContextSourceMode(platformWebMode || (selected.context.recordId && !context.recordId) ? "conversation" : "page");
    setActions(Object.fromEntries(restoredActions.map((item) => [item.id, item])));
    setActionCenterOpen(false);
    setContextManagerOpen(false);
  }, [context.recordId, platformWebMode, websiteMode]);

  const loadWorkRecords = useCallback(async (search = conversationSearch) => {
    try {
      const [conversationPage, actionsResult] = await Promise.all([
        api.listConversationPage({
          status: ["in_progress", "pending_confirmation", "needs_attention", "completed"],
          ...(search.trim() ? { search: search.trim() } : {}),
          limit: 100
        }),
        api.listActionCenter()
      ]);
      setConversationItems(conversationPage.items);
      setActionCenterItems(actionsResult);
      setActions((current) => ({ ...current, ...Object.fromEntries(actionsResult.map((action) => [action.id, action])) }));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "工作记录加载失败");
    }
  }, [conversationSearch]);

  const openActionCenter = useCallback(async () => {
    setContextManagerOpen(false);
    setActionCenterOpen(true);
    await loadWorkRecords();
  }, [loadWorkRecords]);

  const openContextManager = useCallback(async () => {
    setActionCenterOpen(false);
    setContextManagerOpen(true);
    if (!conversation) return;
    try {
      setContextReferences(await api.listConversationReferences(conversation.id));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "上下文记录加载失败");
    }
  }, [conversation]);

  const resumeActionCenterItem = useCallback(async (action: ActionRecord) => {
    setBusy(true);
    setError("");
    try {
      const page = await api.listConversationPage({ limit: 100 });
      const selected = page.items.find(({ id }) => id === action.conversationId);
      if (!selected) throw new Error("该操作所属会话已不可用");
      await restoreConversation(selected);
      window.setTimeout(() => {
        document.querySelector(`[data-action-id="${action.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 80);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "无法打开该操作记录");
    } finally {
      setBusy(false);
    }
  }, [restoreConversation]);

  useWebsiteInactivityFollowUp({
    enabled: websiteMode && connected && !accessRequired,
    conversationId: conversation?.id,
    messages,
    actions: Object.values(actions),
    busy,
    hasDraftInput: Boolean(input.trim() || attachments.length || voiceState !== "idle"),
    activityKey: `${input}:${attachments.map(({ id }) => id).join(",")}:${voiceState}:${resourceNotice}`,
    onReminder: appendWebsiteFollowUp
  });

  const initialize = useCallback(async (context: Partial<SalesforceContext>, activeChannel?: SessionChannel) => {
    const existing = await api.listConversationPage({
      status: ["in_progress", "pending_confirmation", "needs_attention", "completed"],
      limit: 30
    });
    const selected = existing.items[0] ?? await api.createConversationV1(
      context as Record<string, unknown>,
      activeChannel === "website" ? "官网服务咨询" : "AI 助手对话"
    );
    const contextualized = activeChannel === "salesforce" || activeChannel === "development"
      ? await api.updateConversationContext(selected.id, {
          recordId: context.recordId ?? null,
          recordLabel: context.recordLabel ?? null,
          objectApiName: context.objectApiName ?? null,
          accountId: context.accountId ?? null,
          accountLabel: context.accountLabel ?? null,
          contactId: context.contactId ?? null,
          contactLabel: context.contactLabel ?? null,
          opportunityId: context.opportunityId ?? null,
          opportunityLabel: context.opportunityLabel ?? null,
          leadId: context.leadId ?? null,
          leadLabel: context.leadLabel ?? null,
          appName: context.appName ?? null
        })
      : selected;
    setConversation(contextualized);
    const [page, references] = await Promise.all([
      api.listMessagePage(selected.id, { limit: 30 }),
      api.listConversationReferences(selected.id)
    ]);
    setMessageHasMore(page.hasMore);
    setMessageCursor(page.nextCursor);
    setContextReferences(references);
    setContextSourceMode(platformWebMode || activeChannel === "platform_web" ||
      (contextualized.context.recordId && !context.recordId) ? "conversation" : "page");
    if (page.items.length) {
      setMessages(page.items);
      const actionIds = [...new Set(page.items.flatMap((message) => message.actionId ? [message.actionId] : []))];
      const restoredActions = await Promise.all(actionIds.map((id) => api.getAction(id)));
      setActions(Object.fromEntries(restoredActions.map((action) => [action.id, action])));
    }
  }, [platformWebMode]);

  useEffect(() => {
    if (sessionChannel) setChannel(sessionChannel);
  }, [sessionChannel]);

  useEffect(() => {
    const activeChannel = sessionChannel ?? channel;
    if (!connected || !activeChannel || consentRequired || manualSignedOut || initializedRef.current) return;
    if ((activeChannel === "salesforce" || activeChannel === "platform_web") && !businessAccess?.ready) return;
    initializedRef.current = true;
    void initialize(context, activeChannel).catch((cause) => {
      initializedRef.current = false;
      setError(String(cause));
    });
  }, [businessAccess?.ready, channel, connected, consentRequired, context, initialize, manualSignedOut, sessionChannel]);

  useEffect(() => {
    if (!conversation || contextSourceMode !== "page" ||
      (channel !== "salesforce" && channel !== "development")) return;
    const nextContext = {
      recordId: context.recordId ?? null,
      recordLabel: context.recordLabel ?? null,
      objectApiName: context.objectApiName ?? null,
      accountId: context.accountId ?? null,
      accountLabel: context.accountLabel ?? null,
      contactId: context.contactId ?? null,
      contactLabel: context.contactLabel ?? null,
      opportunityId: context.opportunityId ?? null,
      opportunityLabel: context.opportunityLabel ?? null,
      leadId: context.leadId ?? null,
      leadLabel: context.leadLabel ?? null,
      appName: context.appName ?? null
    };
    const syncKey = `${conversation.id}:${JSON.stringify(nextContext)}`;
    if (lastSyncedContextRef.current === syncKey) return;
    lastSyncedContextRef.current = syncKey;
    void api.updateConversationContext(conversation.id, nextContext)
      .then(setConversation)
      .catch((cause) => {
        lastSyncedContextRef.current = "";
        setError(cause instanceof Error ? cause.message : "同步 Salesforce 上下文失败");
      });
  }, [
    channel, context.accountId, context.accountLabel, context.appName,
    context.contactId, context.contactLabel, context.leadId, context.leadLabel,
    context.objectApiName, context.opportunityId, context.opportunityLabel,
    context.recordId, context.recordLabel, contextSourceMode, conversation?.id
  ]);

  const applySession = useCallback((result: SessionBootstrap) => {
    setChannel(result.channel);
    setContext(result.context);
    setPlatformDisplayName(result.platformUser?.displayName ?? "");
    setConsentRequired(Boolean(result.consentRequired));
    setConnected(true);
    setAccessRequired(false);
  }, [setConnected, setConsentRequired, setContext]);

  useEffect(() => {
    if (!connected || websiteMode) return;
    void Promise.all([
      api.getMyProfile(),
      api.getAssistantProfile(),
      api.getAssistantPolicy().catch(() => ({
        attachmentsEnabled: true,
        attachmentBatchWritesEnabled: true,
        resultExport: undefined,
        objectExportPolicies: {}
      }))
    ])
      .then(([profile, assistant, policy]) => {
        setUserProfile(profile);
        setAssistantProfile(assistant);
        setAttachmentsEnabled(policy.attachmentsEnabled);
        setResultExportPolicy(policy.resultExport);
        setObjectExportPolicies(policy.objectExportPolicies);
        setPlatformDisplayName(profile.nickname);
        setMessages((current) => current.length === 1 &&
          current[0]?.role === "assistant" &&
          current[0].content.startsWith("你好，我是 AI 助手。无需先打开")
          ? [{ ...current[0], content: assistant.welcomeMessage }]
          : current);
      })
      .catch(() => undefined);
  }, [connected, websiteMode]);

  useEffect(() => {
    if (!connected) return;
    void api.getVoiceStatus()
      .then(setVoiceAvailability)
      .catch(() => setVoiceAvailability({ available: false, reason: "暂时无法检查语音服务状态" }));
  }, [connected]);

  useEffect(() => {
    if ((websiteMode || platformWebMode || standaloneSalesforceMode) && !localSessionStartedRef.current) {
      localSessionStartedRef.current = true;
      void api.resumeSession(platformWebMode ? "platform_web" : websiteMode ? "website" : "salesforce")
        .catch(() => isLocalDevelopmentRuntime() && !websiteMode
          ? api.createDevSession()
          : Promise.reject(new Error("需要访问验证")))
        .then(applySession)
        .catch(() => (websiteMode || platformWebMode) ? setAccessRequired(true) : setError("等待 Salesforce 会话连接"));
    }
  }, [applySession, platformWebMode, standaloneSalesforceMode, websiteMode]);

  useEffect(() => {
    if (!connected || (channel !== "platform_web" && channel !== "salesforce")) return;
    void refreshBusinessAccess().catch((cause) => setBusinessAccess({
      status: "unavailable",
      ready: false,
      canSelfAuthorize: false,
      reason: cause instanceof Error ? cause.message : "无法检查 Salesforce 业务授权"
    }));
  }, [channel, connected, refreshBusinessAccess]);

  useEffect(() => {
    if (!businessAuthorizationPending) return;
    const check = () => void refreshBusinessAccess().catch(() => undefined);
    const timer = window.setInterval(check, 2_000);
    window.addEventListener("focus", check);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", check);
    };
  }, [businessAuthorizationPending, refreshBusinessAccess]);

  useEffect(() => {
    if (!/尚未绑定当前 Salesforce 用户|Salesforce 用户授权已失效|业务数据授权已失效/.test(error)) return;
    initializedRef.current = false;
    setBusinessAccess((current) => ({
      status: "reauthorization_required",
      ready: false,
      canSelfAuthorize: true,
      ...(current?.salesforceUserId ? { salesforceUserId: current.salesforceUserId } : {}),
      reason: "本人 Salesforce 业务授权已失效，请重新连接"
    }));
  }, [error]);

  const requestWebsiteEmailCode = async () => {
    const email = verificationEmail.trim().toLowerCase();
    if (!email || authBusy) return;
    setAuthBusy(true);
    setError("");
    try {
      const result = platformWebMode
        ? await api.requestPlatformEmailCode(email, "workspace")
        : await api.requestWebsiteEmailCode(email);
      setVerificationEmail(email);
      setMaskedEmail(result.email);
      setVerificationStep("code");
      setVerificationCode("");
      setResendSeconds(result.resendInSeconds);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "验证码发送失败");
    } finally {
      setAuthBusy(false);
    }
  };

  const verifyWebsiteEmailCode = async () => {
    if (!/^\d{6}$/.test(verificationCode) || authBusy) return;
    setAuthBusy(true);
    setError("");
    try {
      applySession(platformWebMode
        ? await api.verifyPlatformEmailCode(verificationEmail, verificationCode, "workspace")
        : await api.verifyWebsiteEmailCode(verificationEmail, verificationCode));
      setVerificationCode("");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "邮箱验证失败");
    } finally {
      setAuthBusy(false);
    }
  };

  const acceptEmbeddedConsent = async () => {
    if (profileBusy) return;
    setProfileBusy(true);
    setError("");
    try {
      await api.acceptEmbeddedConsent();
      initializedRef.current = false;
      setConsentRequired(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "首次使用确认失败");
    } finally {
      setProfileBusy(false);
    }
  };

  const saveUserProfile = async (
    input: Pick<UserProfile, "nickname" | "description" | "avatarSource"> & { avatarUrl?: string }
  ) => {
    setProfileBusy(true);
    setError("");
    try {
      const saved = await api.updateMyProfile(input);
      setUserProfile(saved);
      setPlatformDisplayName(saved.nickname);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "个人资料保存失败");
    } finally {
      setProfileBusy(false);
    }
  };

  const logout = async () => {
    setProfileBusy(true);
    setError("");
    try {
      await api.logout();
      initializedRef.current = false;
      setSettingsOpen(false);
      setConnected(false);
      setUserProfile(undefined);
      setBusinessAccess(undefined);
      setBusinessAuthorizationPending(false);
      if (channel === "salesforce") setManualSignedOut(true);
      else setAccessRequired(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "退出登录失败");
    } finally {
      setProfileBusy(false);
    }
  };

  const logoutAll = async () => {
    setProfileBusy(true);
    setError("");
    try {
      await api.logoutAll();
      initializedRef.current = false;
      setSettingsOpen(false);
      setConnected(false);
      setUserProfile(undefined);
      setBusinessAccess(undefined);
      setBusinessAuthorizationPending(false);
      if (channel === "salesforce") setManualSignedOut(true);
      else setAccessRequired(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "全部退出失败");
    } finally {
      setProfileBusy(false);
    }
  };

  useEffect(() => {
    if (verificationStep !== "code" || resendSeconds <= 0) return;
    const timer = window.setInterval(() => setResendSeconds((value) => Math.max(0, value - 1)), 1_000);
    return () => window.clearInterval(timer);
  }, [resendSeconds, verificationStep]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const preserved = preserveScrollRef.current;
    if (preserved) {
      preserveScrollRef.current = undefined;
      window.requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight - preserved.height + preserved.top;
      });
      return;
    }
    let latestActionId: string | undefined;
    for (const message of messages) if (message.actionId) latestActionId = message.actionId;
    const actionMessage = latestActionId
      ? container.querySelector<HTMLElement>(`[data-action-id="${latestActionId}"]`)
      : null;
    container.scrollTo({
      top: actionMessage ? Math.max(0, actionMessage.offsetTop - container.offsetTop - 4) : container.scrollHeight,
      behavior: "smooth"
    });
  }, [messages, actions, busy, pendingSubmission]);

  const loadOlderMessages = useCallback(async () => {
    if (!conversation || !messageHasMore || !messageCursor || messagePageBusy) return;
    const container = scrollRef.current;
    if (container) preserveScrollRef.current = { height: container.scrollHeight, top: container.scrollTop };
    setMessagePageBusy(true);
    setError("");
    try {
      const page = await api.listMessagePage(conversation.id, { before: messageCursor, limit: 30 });
      const existingIds = new Set(messages.map(({ id }) => id));
      const earlier = page.items.filter(({ id }) => !existingIds.has(id));
      const actionIds = [...new Set(earlier.flatMap((message) => message.actionId ? [message.actionId] : []))]
        .filter((id) => !actions[id]);
      const restoredActions = await Promise.all(actionIds.map((id) => api.getAction(id)));
      setActions((current) => ({ ...current, ...Object.fromEntries(restoredActions.map((action) => [action.id, action])) }));
      setMessages((current) => [...earlier, ...current]);
      setMessageHasMore(page.hasMore);
      setMessageCursor(page.nextCursor);
    } catch (cause) {
      preserveScrollRef.current = undefined;
      setError(cause instanceof Error ? cause.message : "更早消息加载失败");
    } finally {
      setMessagePageBusy(false);
    }
  }, [actions, conversation, messageCursor, messageHasMore, messagePageBusy, messages]);

  useEffect(() => {
    if (!actionCompletion) return;
    const completedActions = [actionCompletion.action, ...(actionCompletion.relatedActions ?? [])];
    setActions((current) => ({
      ...current,
      ...Object.fromEntries(completedActions.map((action) => [action.id, action]))
    }));
    setConversation((current) => current ? {
      ...current,
      status: conversationStatusFromActions(completedActions),
      ...(actionCompletion.context ? { context: actionCompletion.context } : {}),
      updatedAt: new Date().toISOString()
    } : current);
    if (actionCompletion.context) {
      if (!context.recordId) setContextSourceMode("conversation");
      void api.listConversationReferences(actionCompletion.action.conversationId)
        .then(setContextReferences)
        .catch(() => undefined);
    }
    setMessages((current) => current.some((message) => message.id === actionCompletion.assistantMessage.id)
      ? current
      : [...current, actionCompletion.assistantMessage]);
    if (completedActions.some(({ state }) => ["succeeded", "failed", "needs_attention"].includes(state))) {
      setError((current) => ambiguousConnectionErrorPattern.test(current) ? "" : current);
      setCanRetryMessage(false);
    }
    for (const related of actionCompletion.relatedActions ?? []) {
      if (related.state === "executing") {
        void executeAction(related).catch((cause) =>
          setError(cause instanceof Error ? cause.message : "执行领取失败"));
      }
    }
    setActionCompletion(undefined);
  }, [actionCompletion, context.recordId, executeAction, setActionCompletion]);

  useEffect(() => {
    if (!previewCompletion) return;
    setActions((current) => ({ ...current, [previewCompletion.id]: previewCompletion }));
    setPreviewIssues((current) => {
      if (!current[previewCompletion.id]) return current;
      const next = { ...current };
      delete next[previewCompletion.id];
      return next;
    });
    setPreviewCompletion(undefined);
  }, [previewCompletion, setPreviewCompletion]);

  useEffect(() => {
    if (!previewFailure) return;
    for (const requestKey of previewRequestsRef.current) {
      if (requestKey.startsWith(`${previewFailure.actionId}:`)) previewRequestsRef.current.delete(requestKey);
    }
    setPreviewIssues((current) => ({ ...current, [previewFailure.actionId]: previewFailure.error }));
    setPreviewFailure(undefined);
  }, [previewFailure, setPreviewFailure]);

  useEffect(() => {
    if (channel !== "salesforce" && channel !== "platform_web") return;
    for (const action of Object.values(actions)) {
      const requiresPreview = action.state === "pending_confirmation" &&
        (action.plan.operation === "update" || action.plan.operation === "delete") &&
        !action.plan.expectedLastModifiedDate;
      const requestKey = `${action.id}:${action.contentHash}`;
      if (!requiresPreview || previewRequestsRef.current.has(requestKey) || previewIssues[action.id]) continue;
      previewRequestsRef.current.add(requestKey);
      if (channel === "platform_web") {
        void api.previewPlatformAction(action)
          .then((next) => setActions((current) => ({ ...current, [next.id]: next })))
          .catch((cause) => {
            previewRequestsRef.current.delete(requestKey);
            setPreviewIssues((current) => ({
              ...current,
              [action.id]: cause instanceof Error ? cause.message : "读取更新前数据失败"
            }));
          });
      } else {
        void previewAction(action).catch((cause) => {
          previewRequestsRef.current.delete(requestKey);
          setPreviewIssues((current) => ({
            ...current,
            [action.id]: cause instanceof Error ? cause.message : "预览授权失败"
          }));
        });
      }
    }
  }, [actions, channel, previewAction, previewIssues]);

  const retryActionPreview = useCallback((action: ActionRecord) => {
    for (const requestKey of previewRequestsRef.current) {
      if (requestKey.startsWith(`${action.id}:`)) previewRequestsRef.current.delete(requestKey);
    }
    setPreviewIssues((current) => {
      const next = { ...current };
      delete next[action.id];
      return next;
    });
  }, []);

  const completeAction = useCallback(async (action: ActionRecord) => {
    const isSalesforceRead = (currentAction: ActionRecord) =>
      channel === "salesforce" &&
      (currentAction.plan.operation === "query" || currentAction.plan.operation === "aggregate");
    if (channel === "website" || channel === "platform_web" || channel === "development" || isSalesforceRead(action)) {
      const run = async (currentAction: ActionRecord): Promise<void> => {
        if (channel === "salesforce" && !isSalesforceRead(currentAction)) {
          await executeAction(currentAction);
          return;
        }
        const completed = channel === "website"
          ? await api.executeWebsiteAction(currentAction.id)
          : channel === "platform_web"
            ? await api.executePlatformAction(currentAction.id)
            : channel === "salesforce"
              ? await api.executeSalesforceReadAction(currentAction.id)
              : await api.executeDemoAction(currentAction.id);
        const completedActions = [completed.action, ...(completed.relatedActions ?? [])];
        setActions((current) => ({
          ...current,
          ...Object.fromEntries(completedActions.map((item) => [item.id, item]))
        }));
        setMessages((current) => current.some(({ id }) => id === completed.assistantMessage.id)
          ? current
          : [...current, completed.assistantMessage]);
        setConversation((current) => current ? {
          ...current,
          status: conversationStatusFromActions(completedActions),
          ...(completed.context ? { context: completed.context } : {}),
          updatedAt: new Date().toISOString()
        } : current);
        if (completed.context) {
          if (!context.recordId) setContextSourceMode("conversation");
          void api.listConversationReferences(completed.action.conversationId)
            .then(setContextReferences)
            .catch(() => undefined);
        }
        for (const related of completed.relatedActions ?? []) {
          if (related.state === "executing") await run(related);
        }
      };
      await run(action);
      return;
    }
    await executeAction(action);
  }, [channel, context.recordId, executeAction]);

  const sendMessage = async (submittedContent?: string) => {
    const content = (submittedContent ?? input).trim();
    if ((!content && !attachments.length) || !conversation || busy || attachmentBusy) return;
    const submittedAttachments = attachments;
    const attachmentIds = submittedAttachments.map(({ id }) => id);
    setBusy(true);
    setError("");
    setCanRetryMessage(false);
    setPendingSubmission({
      content: content || "请分析附件",
      attachmentNames: submittedAttachments.map(({ fileName }) => fileName)
    });
    if (!submittedContent) setInput("");
    setAttachments([]);
    try {
      const result = await api.sendMessage(conversation.id, content, attachmentIds);
      const assistantMessages = result.assistantMessages?.length
        ? result.assistantMessages
        : [result.assistantMessage];
      setPendingSubmission(undefined);
      setMessages((current) => [...current, result.userMessage, ...assistantMessages]);
      if (genericConversationTitles.has(conversation.title)) {
        const nextTitle = conversationTitleFromMessage(content || attachments[0]?.fileName || "附件分析");
        setConversation((current) => current ? { ...current, title: nextTitle } : current);
        void api.updateConversation(conversation.id, { title: nextTitle })
          .then((updated) => setConversation((current) => current?.id === updated.id ? updated : current))
          .catch(() => undefined);
      }
      clearAttachmentPreviews();
      setAttachments([]);
      const plannedActions = result.actions?.length
        ? result.actions
        : result.action ? [result.action] : [];
      if (plannedActions.length) {
        setActions((current) => ({
          ...current,
          ...Object.fromEntries(plannedActions.map((action) => [action.id, action]))
        }));
        for (const action of plannedActions) {
          if (action.state === "executing") await completeAction(action);
        }
      }
    } catch (cause) {
      setPendingSubmission(undefined);
      setAttachments(submittedAttachments);
      if (!submittedContent) {
        setInput(content);
        setCanRetryMessage(assistantMessageRetryIsSafe(cause));
      }
      setError(cause instanceof Error ? cause.message : "发送失败");
    } finally {
      setBusy(false);
    }
  };

  const uploadAttachments = async (files: File[]) => {
    if (!files.length || !conversation || attachmentBusy || busy) return;
    setError("");
    if (!attachmentsEnabled) {
      setError("管理员已关闭附件上传；你仍可继续发送文字消息");
      return;
    }
    const selectionError = attachmentSelectionError(attachments, files);
    if (selectionError) {
      setError(selectionError);
      return;
    }
    setAttachmentBusy(true);
    try {
      for (const [index, file] of files.entries()) {
        const mimeType = attachmentMimeType(file);
        setAttachmentUploadLabel(`正在上传 ${index + 1}/${files.length}：${file.name}`);
        let uploaded: UploadedAttachment;
        let prepared: Awaited<ReturnType<typeof api.prepareAttachmentUpload>> | undefined;
        try {
          prepared = await api.prepareAttachmentUpload({
            conversationId: conversation.id,
            fileName: file.name,
            mimeType,
            sizeBytes: file.size,
            sha256: await fileSha256(file)
          });
        } catch (cause) {
          if (!(cause instanceof ApiRequestError) || cause.status !== 409) throw cause;
        }
        if (prepared) {
          try {
            await api.uploadPreparedAttachment(prepared.upload.url, prepared.upload.headers, file);
            uploaded = await api.completeAttachmentUpload(prepared.attachment.id);
          } catch (cause) {
            await api.removeAttachment(conversation.id, prepared.attachment.id).catch(() => undefined);
            throw cause;
          }
        } else {
          uploaded = await api.uploadAttachment(conversation.id, {
            fileName: file.name,
            mimeType,
            dataBase64: await fileToBase64(file)
          });
        }
        const previewUrl = mimeType.startsWith("image/") ? URL.createObjectURL(file) : undefined;
        if (previewUrl) attachmentPreviewUrlsRef.current.add(previewUrl);
        setAttachments((current) => [...current, { ...uploaded, ...(previewUrl ? { previewUrl } : {}) }]);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "附件上传失败");
    } finally {
      setAttachmentBusy(false);
      setAttachmentUploadLabel("");
    }
  };

  const removeAttachment = async (attachmentId: string) => {
    if (!conversation || attachmentBusy) return;
    setAttachmentBusy(true);
    setError("");
    try {
      await api.removeAttachment(conversation.id, attachmentId);
      setAttachments((current) => {
        const removed = current.find(({ id }) => id === attachmentId);
        revokeAttachmentPreview(removed?.previewUrl);
        return current.filter(({ id }) => id !== attachmentId);
      });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "移除附件失败");
    } finally {
      setAttachmentBusy(false);
    }
  };

  const stopVoiceRecording = useCallback(() => {
    if (voiceStopTimerRef.current) window.clearTimeout(voiceStopTimerRef.current);
    voiceStopTimerRef.current = null;
    const recorder = mediaRecorderRef.current;
    if (recorder?.state === "recording") recorder.stop();
  }, []);

  const startVoiceRecording = async () => {
    if (voiceState === "recording") {
      stopVoiceRecording();
      return;
    }
    if (voiceState !== "idle" || busy) return;
    if (voiceAvailability?.available === false) {
      setError(voiceAvailability.reason ?? "管理员尚未配置语音转文字模型");
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setError("当前浏览器不支持语音录入，请使用最新版 Chrome 或 Edge");
      return;
    }
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"]
        .find((candidate) => MediaRecorder.isTypeSupported(candidate));
      const recorder = new MediaRecorder(stream, {
        ...(mimeType ? { mimeType } : {}),
        audioBitsPerSecond: 24_000
      });
      mediaStreamRef.current = stream;
      mediaRecorderRef.current = recorder;
      voiceChunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size) voiceChunksRef.current.push(event.data);
      };
      recorder.onerror = () => {
        stream.getTracks().forEach((track) => track.stop());
        setVoiceState("idle");
        setError("录音中断，请重新尝试");
      };
      recorder.onstop = () => {
        if (voiceStopTimerRef.current) window.clearTimeout(voiceStopTimerRef.current);
        voiceStopTimerRef.current = null;
        stream.getTracks().forEach((track) => track.stop());
        const audioType = recorder.mimeType.split(";")[0] || "audio/webm";
        const extension = ({ "audio/ogg": "ogg", "audio/mp4": "m4a", "audio/mpeg": "mp3", "audio/wav": "wav" } as Record<string, string>)[audioType] ?? "webm";
        const audio = new Blob(voiceChunksRef.current, { type: audioType });
        setVoiceState("transcribing");
        void fileToBase64(audio)
          .then((dataBase64) => api.transcribeVoice({ fileName: `sales-voice.${extension}`, mimeType: audioType, dataBase64 }))
          .then(({ text }) => {
            setInput((current) => [current.trim(), text.trim()].filter(Boolean).join(" "));
            window.setTimeout(() => composerRef.current?.focus(), 0);
          })
          .catch((cause) => setError(cause instanceof Error ? cause.message : "语音转写失败"))
          .finally(() => {
            setVoiceState("idle");
            mediaRecorderRef.current = null;
            mediaStreamRef.current = null;
            voiceChunksRef.current = [];
          });
      };
      recorder.start(1_000);
      setVoiceState("recording");
      voiceStopTimerRef.current = window.setTimeout(stopVoiceRecording, maxVoiceRecordingMs);
    } catch (cause) {
      setVoiceState("idle");
      setError(microphoneStartErrorMessage(cause));
    }
  };

  useEffect(() => () => {
    if (voiceStopTimerRef.current) window.clearTimeout(voiceStopTimerRef.current);
    const recorder = mediaRecorderRef.current;
    if (recorder?.state === "recording") {
      recorder.onstop = null;
      recorder.stop();
    }
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
  }, []);

  const confirm = async (action: ActionRecord) => {
    setBusy(true);
    try {
      const next = await api.confirmAction(action);
      setActions((current) => ({ ...current, [action.id]: next }));
      if (next.state === "executing") await completeAction(next);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "确认失败");
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (action: ActionRecord) => {
    setBusy(true);
    setError("");
    try {
      const next = await api.cancelAction(action.id);
      setActions((current) => ({ ...current, [action.id]: next }));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "取消操作失败");
    } finally {
      setBusy(false);
    }
  };

  const revise = async (action: ActionRecord, fields: Record<string, unknown>) => {
    setBusy(true);
    setError("");
    try {
      const next = await api.reviseAction(action, {
        setFields: fields,
        summary: "根据预览中的编辑更新字段"
      });
      setActions((current) => ({ ...current, [action.id]: next }));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "更新预览失败");
    } finally {
      setBusy(false);
    }
  };

  const reviseBatch = async (action: ActionRecord, batchEdits: Array<{ sourceIndex: number; recordId?: string; excluded: boolean }>) => {
    setBusy(true);
    setError("");
    try {
      const next = await api.reviseBatchAction(action, batchEdits, "根据预览选择调整本次批量处理范围");
      setActions((current) => ({ ...current, [action.id]: next }));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "批量预览更新失败");
    } finally {
      setBusy(false);
    }
  };

  const retryFailed = async (action: ActionRecord) => {
    setBusy(true);
    setError("");
    try {
      const next = await api.retryFailedAction(action.id);
      setActions((current) => ({ ...current, [action.id]: next }));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "失败项续跑准备失败");
    } finally {
      setBusy(false);
    }
  };

  const createNewConversation = useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      const created = await api.createConversationV1(
        (channel === "salesforce" || channel === "development") ? context as Record<string, unknown> : {},
        channel === "website" ? "官网服务咨询" : "新工作会话"
      );
      setConversation(created);
      setMessages([createWelcomeMessage(websiteMode)]);
      setActions({});
      setMessageHasMore(false);
      setMessageCursor(undefined);
      setContextReferences([]);
      setContextSourceMode(platformWebMode || channel === "platform_web" ? "conversation" : "page");
      setActionCenterOpen(false);
      setContextManagerOpen(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "新建会话失败");
    } finally {
      setBusy(false);
    }
  }, [channel, context, platformWebMode, websiteMode]);

  const renameConversation = useCallback(async (item: Conversation) => {
    const title = window.prompt("请输入新的会话名称", item.title)?.trim();
    if (!title || title === item.title) return;
    try {
      const updated = await api.updateConversation(item.id, { title });
      setConversationItems((current) => current.map((candidate) => candidate.id === updated.id ? updated : candidate));
      setConversation((current) => current?.id === updated.id ? updated : current);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "会话重命名失败");
    }
  }, []);

  const archiveConversation = useCallback(async (item: Conversation) => {
    try {
      await api.updateConversation(item.id, { status: "archived" });
      setConversationItems((current) => current.filter(({ id }) => id !== item.id));
      if (conversation?.id === item.id) await createNewConversation();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "会话归档失败");
    }
  }, [conversation?.id, createNewConversation]);

  const selectConversationReference = useCallback(async (reference: ConversationRecordReference) => {
    if (!conversation) return;
    setBusy(true);
    setError("");
    try {
      const relatedContext = reference.objectApiName === "Account"
        ? { accountId: reference.recordId, accountLabel: reference.recordLabel ?? null }
        : reference.objectApiName === "Contact"
          ? { contactId: reference.recordId, contactLabel: reference.recordLabel ?? null }
          : reference.objectApiName === "Opportunity"
            ? { opportunityId: reference.recordId, opportunityLabel: reference.recordLabel ?? null }
            : reference.objectApiName === "Lead"
              ? { leadId: reference.recordId, leadLabel: reference.recordLabel ?? null }
              : {};
      const updated = await api.updateConversationContext(conversation.id, {
        recordId: reference.recordId,
        recordLabel: reference.recordLabel ?? null,
        objectApiName: reference.objectApiName,
        source: "user_selected",
        ...relatedContext
      });
      setConversation(updated);
      setContextSourceMode("conversation");
      setContextReferences(await api.listConversationReferences(conversation.id));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "切换会话上下文失败");
    } finally {
      setBusy(false);
    }
  }, [conversation]);

  const clearConversationContext = useCallback(async () => {
    if (!conversation) return;
    setBusy(true);
    setError("");
    try {
      const updated = await api.updateConversationContext(conversation.id, {
        recordId: null,
        recordLabel: null,
        objectApiName: null,
        accountId: null,
        accountLabel: null,
        contactId: null,
        contactLabel: null,
        opportunityId: null,
        opportunityLabel: null,
        leadId: null,
        leadLabel: null
      });
      setConversation(updated);
      setContextSourceMode("page");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "清除会话上下文失败");
    } finally {
      setBusy(false);
    }
  }, [conversation]);

  const effectiveContext = selectEffectiveContext(
    context,
    conversation?.context,
    sessionChannel ?? channel,
    contextSourceMode
  );
  const contextFocus = describeContextFocus(effectiveContext, contextSourceMode);
  const latestMessageIdByAction = useMemo(() => {
    const latest = new Map<string, string>();
    for (const message of messages) if (message.actionId) latest.set(message.actionId, message.id);
    return latest;
  }, [messages]);
  const activeActionGroup = useMemo(() => {
    const groups = new Map<string, ActionRecord[]>();
    for (const action of Object.values(actions)) {
      if (!action.groupId || !["pending_confirmation", "needs_attention"].includes(action.state)) continue;
      groups.set(action.groupId, [...(groups.get(action.groupId) ?? []), action]);
    }
    return [...groups.values()]
      .filter((items) => items.length >= 2 && items.length === items[0]?.groupSize)
      .sort((left, right) => Date.parse(right[0]!.updatedAt) - Date.parse(left[0]!.updatedAt))[0]
      ?.sort((left, right) => (left.groupSequence ?? 0) - (right.groupSequence ?? 0));
  }, [actions]);
  const groupedConversationItems = useMemo(() => {
    const groups = new Map<string, Conversation[]>();
    for (const item of conversationItems) {
      const group = conversationDateGroup(item.lastMessageAt ?? item.updatedAt);
      groups.set(group, [...(groups.get(group) ?? []), item]);
    }
    return ["今天", "昨天", "最近7天", "更早"]
      .flatMap((group) => groups.has(group) ? [{ group, items: groups.get(group)! }] : []);
  }, [conversationItems]);
  const attentionActions = useMemo(() =>
    actionCenterItems.filter(({ state }) => ["pending_confirmation", "needs_attention", "failed"].includes(state)),
  [actionCenterItems]);
  const conversationStatusLabel = conversation?.status === "pending_confirmation"
    ? "待确认"
    : conversation?.status === "needs_attention"
      ? "需要处理"
        : conversation?.status === "completed"
          ? "已完成"
          : "进行中";
  const errorPresentation = error ? assistantErrorPresentation(error) : undefined;
  const activeSessionChannel = sessionChannel ?? channel;
  const requiresBusinessAccess = connected &&
    (activeSessionChannel === "salesforce" || activeSessionChannel === "platform_web");

  if ((websiteMode || platformWebMode) && accessRequired) {
    return (
      <main className="demo-access-shell">
        <section className="demo-access-panel" aria-labelledby="demo-access-title">
          <div className="brand demo-access-brand"><span className="brand__mark"><SparklesIcon size={23} /></span><strong>{platformWebMode ? "AI 助手" : "官网服务助手"}</strong></div>
          <h1 id="demo-access-title">{platformWebMode ? "企业账号登录" : "邮箱验证"}</h1>
          {verificationStep === "email" ? (
            <form onSubmit={(event) => { event.preventDefault(); void requestWebsiteEmailCode(); }}>
              <p>{platformWebMode ? "请使用管理员邀请的企业邮箱完成验证。登录后，业务数据仍按您本人的 Salesforce 权限访问。" : "请使用有效邮箱完成验证，验证后可咨询产品方案、提交服务需求并获取技术资料。"}</p>
              <label htmlFor="verification-email">{platformWebMode ? "受邀企业邮箱" : "工作邮箱或常用邮箱"}</label>
              <input
                id="verification-email"
                type="email"
                autoComplete="email"
                value={verificationEmail}
                onChange={(event) => { setVerificationEmail(event.target.value); setError(""); }}
                placeholder="name@company.com"
                required
                autoFocus
              />
              {error ? <div className="error-banner demo-access-error" role="alert">{error}</div> : null}
              <button className="button button--primary demo-access-submit" type="submit" disabled={!verificationEmail.trim() || authBusy}>
                {authBusy ? "正在发送…" : "发送验证码"}
              </button>
            </form>
          ) : (
            <form onSubmit={(event) => { event.preventDefault(); void verifyWebsiteEmailCode(); }}>
              <p>验证码已发送至 <strong>{maskedEmail}</strong>，请查看邮箱并在 10 分钟内完成验证。</p>
              <label htmlFor="verification-code">邮箱验证码</label>
              <input
                id="verification-code"
                className="verification-code-input"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={verificationCode}
                onChange={(event) => { setVerificationCode(event.target.value.replace(/\D/g, "").slice(0, 6)); setError(""); }}
                placeholder="请输入 6 位验证码"
                required
                autoFocus
              />
              {error ? <div className="error-banner demo-access-error" role="alert">{error}</div> : null}
              <button className="button button--primary demo-access-submit" type="submit" disabled={verificationCode.length !== 6 || authBusy}>
                {authBusy ? "正在验证…" : "验证并进入"}
              </button>
              <div className="verification-actions">
                <button type="button" onClick={() => { setVerificationStep("email"); setVerificationCode(""); setError(""); }}>更换邮箱</button>
                <button type="button" onClick={() => void requestWebsiteEmailCode()} disabled={resendSeconds > 0 || authBusy}>
                  {resendSeconds > 0 ? `${resendSeconds} 秒后可重新发送` : "重新发送验证码"}
                </button>
              </div>
            </form>
          )}
          {platformWebMode ? <button className="button button--secondary button--full" type="button" onClick={() => void api.startSalesforceSso("/workspace").then((result) => window.location.assign(result.authorizationUrl)).catch((cause) => setError(cause instanceof Error ? cause.message : "Salesforce 登录不可用"))}>使用 Salesforce 企业登录</button> : null}
          <small>{platformWebMode ? "所有查询与写入均使用本人 Salesforce 权限；写入前会先展示预览并等待确认。" : "邮箱仅用于本次服务识别与业务联系；涉及业务提交时会先展示内容并请您确认。"}</small>
        </section>
      </main>
    );
  }

  if (!websiteMode && (consentRequired || manualSignedOut)) {
    const assistantName = assistantProfile?.name ?? "AI 助手";
    return <main className="assistant-activation-shell">
      <section className="assistant-activation-card">
        <div className="assistant-activation-card__brand">
          <span>{assistantProfile?.avatarUrl
            ? <img src={assistantProfile.avatarUrl} alt="" />
            : <SparklesIcon size={26} />}</span>
          <div><strong>{assistantName}</strong><small>Salesforce 当前用户安全连接</small></div>
        </div>
        <h1>{consentRequired ? "首次使用确认" : "已退出 AI 助手"}</h1>
        <p>{consentRequired
          ? "管理员已为当前 Salesforce 用户开通 AI 使用资格。继续后，将使用您本人的 Salesforce 权限运行已分配场景；写入操作仍需您确认。"
          : "您仍保持 Salesforce 登录状态。需要继续工作时，可以重新进入 AI 助手。"}</p>
        {consentRequired ? <ul>
          <li>不会使用 Integration User 代替您访问业务数据</li>
          <li>只能运行管理员已发布并分配的场景</li>
          <li>退出 AI 助手不会退出 Salesforce</li>
        </ul> : null}
        {error ? <div className="error-banner" role="alert">{error}</div> : null}
        <button
          className="button button--primary button--full"
          type="button"
          disabled={profileBusy}
          onClick={() => void (consentRequired
            ? acceptEmbeddedConsent()
            : Promise.resolve().then(() => {
              initializedRef.current = false;
              setManualSignedOut(false);
            }))}
        >{profileBusy ? "处理中…" : consentRequired ? "继续使用 AI 助手" : "重新进入 AI 助手"}</button>
      </section>
    </main>;
  }

  if (!websiteMode && requiresBusinessAccess && !businessAccess?.ready) {
    const checking = !businessAccess;
    const unavailable = businessAccess?.status === "unavailable" || businessAccess?.canSelfAuthorize === false;
    const reconnecting = businessAccess?.status === "reauthorization_required";
    const assistantName = assistantProfile?.name ?? "AI 助手";
    return <main className="assistant-activation-shell">
      <section className="assistant-activation-card" aria-labelledby="business-authorization-title">
        <div className="assistant-activation-card__brand">
          <span>{assistantProfile?.avatarUrl
            ? <img src={assistantProfile.avatarUrl} alt="" />
            : <SparklesIcon size={26} />}</span>
          <div><strong>{assistantName}</strong><small>当前用户权限连接</small></div>
        </div>
        <h1 id="business-authorization-title">{checking
          ? "正在检查 Salesforce 权限"
          : businessAuthorizationPending
            ? "请完成本人授权"
            : reconnecting
              ? "重新连接 Salesforce"
              : "连接本人 Salesforce"}</h1>
        <p>{checking
          ? "正在确认当前账号是否已具备可持续使用的本人业务授权。"
          : businessAuthorizationPending
            ? "授权窗口已经打开。请使用当前 Salesforce 账号完成授权，完成后本页面会自动恢复。"
            : businessAccess?.reason ?? "首次使用前需要连接本人 Salesforce 业务权限。"}</p>
        {!checking && !unavailable ? <ul>
          <li>无需管理员生成或转发一次性授权链接</li>
          <li>不会要求填写 Consumer Key、Secret 或激活码</li>
          <li>所有业务数据仍受本人 CRUD、FLS、共享规则和记录权限控制</li>
        </ul> : null}
        {error ? <div className="error-banner" role="alert">{error}</div> : null}
        <div className="business-authorization-actions">
          {!checking && !unavailable ? <button
            className="button button--primary button--full"
            type="button"
            disabled={businessAuthorizationBusy}
            onClick={() => void beginBusinessAuthorization()}
          >{businessAuthorizationBusy
            ? "正在准备安全授权…"
            : businessAuthorizationPending
              ? "重新打开授权窗口"
              : reconnecting
                ? "重新连接 Salesforce"
                : "连接 Salesforce"}</button> : null}
          {businessAuthorizationPending ? <button
            className="button button--secondary button--full"
            type="button"
            onClick={() => void refreshBusinessAccess()}
          >我已完成，立即检查</button> : null}
        </div>
        {unavailable ? <small>当前账号的绑定或授权服务尚未就绪，请联系客户管理员检查用户绑定、AI 使用资格和 User Access ECA。</small> : null}
      </section>
    </main>;
  }

  return (
    <main className={[
      "assistant-shell",
      websiteMode ? "assistant-shell--website" : "",
      embeddedSalesforceMode ? "assistant-shell--embedded" : "",
      platformWebMode ? "assistant-shell--workspace" : "",
      standaloneSalesforceMode ? "assistant-shell--standalone-salesforce" : ""
    ].filter(Boolean).join(" ")}>
      <header className="assistant-header">
        <div className="brand">
          <span className="brand__mark">
            {assistantProfile?.avatarUrl
              ? <img src={assistantProfile.avatarUrl} alt="" />
              : <SparklesIcon size={23} />}
          </span>
          <strong>{websiteMode ? "官网服务助手" : assistantProfile?.name ?? "AI 助手"}</strong>
        </div>
        {websiteMode ? null : <div className="header-actions">
          <button
            type="button"
            className="header-tool-button action-center-button"
            aria-label="历史会话与工作记录"
            aria-pressed={actionCenterOpen}
            onClick={() => void openActionCenter()}
          ><HistoryIcon /><span>会话</span></button>
          <button
            type="button"
            className="header-tool-button"
            aria-label="上下文管理"
            aria-pressed={contextManagerOpen}
            onClick={() => void openContextManager()}
          ><LayersIcon /><span>上下文</span></button>
          <button
            type="button"
            className="header-tool-button"
            aria-label="个人设置"
            aria-pressed={settingsOpen}
            onClick={() => setSettingsOpen((current) => !current)}
          ><SettingsIcon /><span>设置</span></button>
        </div>}
      </header>
      {settingsOpen && userProfile && assistantProfile && !websiteMode ? <AssistantUserSettings
        profile={userProfile}
        assistantProfile={assistantProfile}
        channel={channel ?? sessionChannel ?? "salesforce"}
        salesforceAvatarUrl={context.userAvatarUrl}
        busy={profileBusy}
        onSave={saveUserProfile}
        onLogout={logout}
        onLogoutAll={logoutAll}
        onClose={() => setSettingsOpen(false)}
      /> : null}
      {actionCenterOpen ? (
        <aside className="action-center work-records" aria-label="工作记录">
          <header><div><strong>工作记录</strong><span>切换历史会话，继续待确认或异常任务</span></div><button type="button" aria-label="关闭工作记录" onClick={() => setActionCenterOpen(false)}>×</button></header>
          <div className="work-records__tabs" role="tablist" aria-label="工作记录分类">
            <button type="button" role="tab" aria-selected={workRecordTab === "conversations"} onClick={() => setWorkRecordTab("conversations")}>我的会话</button>
            <button type="button" role="tab" aria-selected={workRecordTab === "attention"} onClick={() => setWorkRecordTab("attention")}>
              待我处理{attentionActions.length ? <span>{attentionActions.length}</span> : null}
            </button>
          </div>
          {workRecordTab === "conversations" ? <div className="work-records__conversations">
            <form className="work-records__search" onSubmit={(event) => { event.preventDefault(); void loadWorkRecords(conversationSearch); }}>
              <input value={conversationSearch} onChange={(event) => setConversationSearch(event.target.value)} placeholder="搜索会话、客户或项目" aria-label="搜索工作记录" />
              <button type="submit">搜索</button>
              <button type="button" className="work-records__new" onClick={() => void createNewConversation()}>新建</button>
            </form>
            <div className="action-center__list">
              {groupedConversationItems.length ? groupedConversationItems.map(({ group, items }) => <section className="conversation-group" key={group}>
                <h3>{group}</h3>
                {items.map((item) => <article key={item.id} className={`conversation-item ${conversation?.id === item.id ? "conversation-item--active" : ""}`}>
                  <button type="button" className="conversation-item__main" onClick={() => void restoreConversation(item)}>
                    <strong>{item.title}</strong>
                    <span>{formatConversationTime(item.lastMessageAt ?? item.updatedAt)}</span>
                    <small>{item.status === "pending_confirmation" ? "待确认" : item.status === "needs_attention" ? "需要处理" : item.status === "completed" ? "已完成" : "进行中"}{item.pendingItemCount ? ` · ${item.pendingItemCount} 项待处理` : ""}</small>
                  </button>
                  <div className="conversation-item__actions">
                    <button type="button" aria-label={`重命名 ${item.title}`} title="重命名" onClick={() => void renameConversation(item)}>编辑</button>
                    <button type="button" aria-label={`归档 ${item.title}`} title="归档" onClick={() => void archiveConversation(item)}>归档</button>
                  </div>
                </article>)}
              </section>) : <p className="action-center__empty">暂无匹配的工作会话</p>}
            </div>
          </div> : <div className="action-center__list">
            {attentionActions.length ? attentionActions.map((action) => {
              const count = action.plan.batchRecords?.length ?? action.plan.batchCreate?.items.filter(({ excluded }) => !excluded).length ?? 1;
              return <article key={action.id} className={`action-center__item action-center__item--${action.state}`}>
                <div><strong>{action.plan.summary}</strong><span>{formatConversationTime(action.updatedAt)}</span></div>
                <p>{count > 1 ? `${count} 条记录 · ` : ""}{actionStateLabels[action.state]}</p>
                {action.error ? <small>{action.error}</small> : null}
                <button type="button" disabled={busy} onClick={() => void resumeActionCenterItem(action)}>继续处理</button>
              </article>;
            }) : <p className="action-center__empty">当前没有待确认或需要处理的任务</p>}
          </div>}
        </aside>
      ) : null}
      {contextManagerOpen && !websiteMode ? <ConversationContextPanel
        liveContext={context}
        conversationContext={conversation?.context ?? {}}
        references={contextReferences}
        mode={contextSourceMode}
        pageContextAvailable={!platformWebMode}
        busy={busy}
        onModeChange={setContextSourceMode}
        onSelectReference={selectConversationReference}
        onClearConversationContext={clearConversationContext}
        onClose={() => setContextManagerOpen(false)}
      /> : null}
      <section className="assistant-session-panel">
        <div className="connection-row">
          <span className={connected ? "status-dot" : "status-dot status-dot--offline"} />
          {channel === "development"
            ? "开发环境 · Salesforce 尚未连接"
            : websiteMode
              ? connected ? "官网服务会话已连接" : "正在建立服务会话"
              : connected ? "已连接到 Salesforce" : "等待 Salesforce 连接"}
        </div>
        {websiteMode ? null : <div className="conversation-strip" aria-label="当前会话">
          <div>
            <span>当前会话 · {conversationStatusLabel}</span>
            <strong>{conversation?.title ?? "正在准备会话"}</strong>
          </div>
          <time dateTime={conversation?.updatedAt}>{conversation ? formatConversationTime(conversation.updatedAt) : ""}</time>
          <button type="button" disabled={!conversation || busy} onClick={() => void createNewConversation()}>＋ 新会话</button>
        </div>}
        {websiteMode || !contextFocus ? null : <button
          type="button"
          className="context-focus-chip"
          aria-label={`${contextFocus.prefix}：${contextFocus.label}，打开上下文管理`}
          onClick={() => void openContextManager()}
        >
          <span>{contextFocus.prefix}</span>
          <strong>{contextFocus.label}</strong>
          <span aria-hidden="true">管理</span>
        </button>}
      </section>
      <div className="message-list" ref={scrollRef} aria-live="polite">
        {messageHasMore ? <button type="button" className="load-older-messages" disabled={messagePageBusy} onClick={() => void loadOlderMessages()}>
          {messagePageBusy ? "正在加载…" : "查看更早消息"}
        </button> : null}
        {messages.map((message, index) => {
          const action = message.actionId ? actions[message.actionId] : undefined;
          const isLatestActionMessage = action
            ? latestMessageIdByAction.get(action.id) === message.id
            : false;
          const outcomeActionMessage = isOutcomeActionMessage(websiteMode, message.role, action?.state);
          if (outcomeActionMessage && !isLatestActionMessage) return null;
          return <Fragment key={message.id}>
            {shouldShowMessageTime(messages, index) ? (
              <time className="message-time" dateTime={message.createdAt}>
                {formatConversationTime(message.createdAt)}
              </time>
            ) : null}
            <article
              className={`message message--${message.role}`}
              data-action-id={message.actionId}
              aria-label={`${message.role === "user" ? "你的消息" : message.role === "assistant" ? "AI 助手消息" : "系统消息"}，${formatConversationTime(message.createdAt)}`}
            >
              {message.role === "assistant" ? <span className="assistant-avatar">
                {assistantProfile?.avatarUrl
                  ? <img src={assistantProfile.avatarUrl} alt="" />
                  : <SparklesIcon size={17} />}
              </span> : null}
              <div className="message__body">
                {outcomeActionMessage
                  ? null
                  : websiteMode && message.role === "assistant"
                    ? <WebsiteMessageContent content={message.content} onResourceOpen={(title) => setResourceNotice(`已打开“${title}”官方资料；如浏览器未自动保存，可在新页面使用下载按钮。`)} />
                    : <p>{message.content}</p>}
                {action && isLatestActionMessage ? (
                  <ActionProposal
                    action={action}
                    busy={busy}
                    demo={websiteMode}
                    onConfirm={() => void confirm(action)}
                    onCancel={() => void cancel(action)}
                    onRevise={(fields) => void revise(action, fields)}
                    onReviseBatch={(batchEdits) => void reviseBatch(action, batchEdits)}
                    onRetryFailed={() => void retryFailed(action)}
                    previewError={previewIssues[action.id]}
                    onRetryPreview={() => retryActionPreview(action)}
                    onSuggestion={(suggestion) => void sendMessage(suggestion)}
                    resultExport={resultExportPolicy}
                    objectExportPolicy={objectExportPolicies[action.plan.objectApiName] ?? "inherit"}
                  />
                ) : null}
              </div>
              {message.role === "user" ? <UserAvatar
                displayName={userProfile?.nickname || context.userDisplayName || platformDisplayName || context.visitorEmail}
                avatarUrl={userProfile?.avatarUrl || context.userAvatarUrl}
              /> : null}
            </article>
          </Fragment>;
        })}
        {pendingSubmission ? <article className="message message--user message--pending" aria-label="你的消息，已提交">
          <div className="message__body">
            <p>{pendingSubmission.content}</p>
            {pendingSubmission.attachmentNames.length ? <small className="message__submission-files">
              附件：{pendingSubmission.attachmentNames.join("、")}
            </small> : null}
            <small className="message__submission-state">已提交</small>
          </div>
          <UserAvatar
            displayName={userProfile?.nickname || context.userDisplayName || platformDisplayName || context.visitorEmail}
            avatarUrl={userProfile?.avatarUrl || context.userAvatarUrl}
          />
        </article> : null}
        {busy && !Object.values(actions).some((action) => action.state === "executing" && ["query", "aggregate"].includes(action.plan.operation))
          ? <GeneratingIndicator hasAttachments={Boolean(pendingSubmission?.attachmentNames.length)} />
          : null}
      </div>
      {activeActionGroup ? <div className="action-group-bar" role="group" aria-label="连续操作确认">
        <div><strong>{activeActionGroup.length} 项连续操作待确认</strong><span>可逐项调整，也可按当前预览全部执行</span></div>
        <button type="button" className="button button--secondary" disabled={busy} onClick={() => void sendMessage("取消全部")}>全部取消</button>
        <button type="button" className="button button--primary" disabled={busy || activeActionGroup.some(requiresClientPreview)} onClick={() => void sendMessage("确认全部")}>确认全部</button>
      </div> : null}
      {resourceNotice ? <div className="success-banner" role="status"><span aria-hidden="true">✓</span>{resourceNotice}</div> : null}
      {error && errorPresentation ? <div className="error-banner error-banner--assistant" role="alert">
        <span className="error-banner__icon" aria-hidden="true">!</span>
        <span className="error-banner__copy">
          <strong>{errorPresentation.title}</strong>
          <small>{websiteMode ? toCustomerFacingError(errorPresentation.description) : errorPresentation.description}</small>
        </span>
        {canRetryMessage ? <button type="button" className="error-banner__retry" disabled={busy} onClick={() => void sendMessage()}>
          重新发送
        </button> : null}
        <button type="button" className="error-banner__dismiss" aria-label="关闭错误提示" onClick={() => {
          setError("");
          setCanRetryMessage(false);
        }}>×</button>
      </div> : null}
      <footer className="composer-wrap">
        {attachments.length ? <div className="attachment-tray" aria-label="待随消息提交的附件">
          {attachments.map((attachment) => <span className="attachment-chip" key={attachment.id}>
            <span className={`attachment-chip__preview${attachment.previewUrl ? " attachment-chip__preview--image" : ""}`} aria-hidden="true">
              {attachment.previewUrl
                ? <img src={attachment.previewUrl} alt="" />
                : attachmentTypeLabel(attachment.mimeType)}
            </span>
            <span className="attachment-chip__copy">
              <strong>{attachment.fileName}</strong>
              <small>{attachmentTypeLabel(attachment.mimeType)} · {formatAttachmentSize(attachment.size)} · 已就绪</small>
            </span>
            <button type="button" aria-label={`移除附件 ${attachment.fileName}`} title="移除附件" onClick={() => void removeAttachment(attachment.id)} disabled={attachmentBusy}>×</button>
          </span>)}
        </div> : null}
        {voiceState !== "idle" ? <div className={`voice-status voice-status--${voiceState}`} role="status">
          <span aria-hidden="true" />{voiceState === "recording" ? "正在聆听，再次点击麦克风结束" : "正在识别语音…"}
        </div> : null}
        {attachmentBusy ? <div className="attachment-upload-status" role="status">
          <span aria-hidden="true" />{attachmentUploadLabel || "正在上传附件，请稍候…"}
        </div> : null}
        <div className="composer">
          {attachmentsEnabled ? <AttachmentPicker
            disabled={!conversation || attachmentBusy || attachments.length >= maxConversationAttachments || busy}
            busy={attachmentBusy}
            onSelect={uploadAttachments}
          /> : <span
            className="icon-button icon-button--policy-disabled"
            aria-label="附件上传已由管理员关闭"
            title="附件上传已由管理员关闭"
          ><PaperclipIcon /></span>}
          <textarea
            ref={composerRef}
            value={input}
            onChange={(event) => {
              setInput(event.target.value);
              if (error) setError("");
              if (canRetryMessage) setCanRetryMessage(false);
            }}
            onPaste={(event) => {
              const files = extractClipboardFiles(event.clipboardData);
              if (!files.length) return;
              if (!event.clipboardData.getData("text/plain")) event.preventDefault();
              void uploadAttachments(files);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); void sendMessage(); }
            }}
            placeholder={websiteMode ? "请描述项目需求或遇到的问题…" : "请输入问题或指令…"}
            rows={1}
            aria-label="消息输入"
          />
          <button
            type="button"
            className={`icon-button voice-button${voiceState === "recording" ? " voice-button--recording" : ""}`}
            aria-label={voiceState === "recording" ? "停止录音" : "语音输入"}
            title={voiceState === "recording"
              ? "结束录音并转写"
              : voiceAvailability?.available === false
                ? voiceAvailability.reason ?? "管理员尚未配置语音转文字模型"
                : "语音输入，转写后可继续编辑"}
            aria-pressed={voiceState === "recording"}
            disabled={!conversation || busy || voiceState === "transcribing" || voiceAvailability?.available === false}
            onClick={() => void startVoiceRecording()}
          ><MicIcon /></button>
          <button type="button" className="send-button" aria-label="发送" onClick={() => void sendMessage()} disabled={(!input.trim() && !attachments.length) || busy || attachmentBusy || voiceState !== "idle"}><SendIcon size={18} /></button>
        </div>
        <p>{websiteMode
          ? "可粘贴图片或文件 · AI 生成内容可能存在偏差，业务提交前会再次确认"
          : "Enter 发送 · Shift+Enter 换行 · 可粘贴图片或文件 · 写入操作始终需要确认"}</p>
      </footer>
    </main>
  );
}
